/**
 * Announcement bar: close button, sessionStorage, --height-bar
 */
class AnnouncementBar extends HTMLElement {
  constructor() {
    super();
    const close = this.querySelector(".announcement-bar-close");
    if (close) {
      close.addEventListener("click", () => {
        this.style.display = "none";
        try {
          sessionStorage.setItem("announcement_bar", "false");
        } catch (e) {}
        const section = document.querySelector(".section-announcement-bar");
        if (section) section.classList.add("hidden");
        document.body.style.setProperty("--height-bar", "0px");
      });
    }
  }
}

/**
 * Slide section: Swiper wrapper for announcement bar (vertical slider)
 * Inits in connectedCallback so it works when section is re-rendered (e.g. adding blocks in editor).
 */
class SlideSection extends HTMLElement {
  constructor() {
    super();
    this.globalSlide = null;
  }

  connectedCallback() {
    if (this.globalSlide) return;
    var self = this;
    function init() {
      if (!window.Swiper || self.globalSlide) return;
      self.globalSlide = self._createSwiper();
    }
    if (window.Swiper) {
      requestAnimationFrame(function() { requestAnimationFrame(init); });
    } else {
      window.addEventListener("load", function() { requestAnimationFrame(init); });
    }
  }

  disconnectedCallback() {
    if (this.globalSlide && this.globalSlide.destroy) {
      this.globalSlide.destroy(true, true);
      this.globalSlide = null;
    }
  }

  _createSwiper() {
    const el = this;
    const autoplay = el.dataset.autoplay === "true";
    const autoplaySpeed = el.dataset.autoplaySpeed ? parseInt(el.dataset.autoplaySpeed, 10) * 1000 : 3000;
    const direction = el.dataset.direction || "horizontal";
    const loop = el.dataset.loop === "true";
    const speed = parseInt(el.dataset.speed, 10) || 400;
    const spacing = parseInt(el.dataset.spacing, 10) || 0;

    var container = el.closest(".announcement-slide");
    var nextEl = (container || el).querySelector(".swiper-button-next");
    var prevEl = (container || el).querySelector(".swiper-button-prev");
    return new Swiper(el, {
      slidesPerView: 1,
      spaceBetween: spacing,
      autoplay: autoplay ? { delay: autoplaySpeed } : false,
      direction: direction,
      loop: loop,
      speed: speed,
      navigation: {
        nextEl: nextEl,
        prevEl: prevEl,
      },
      on: {
        init: function(swiper) {
          if (direction === "vertical") {
            var maxH = 64;
            var setHeight = function() {
              var slide = el.querySelector(".swiper-slide-active") || el.querySelector(".swiper-slide:not(.swiper-slide-duplicate)");
              if (slide) {
                var h = Math.min(maxH, slide.offsetHeight);
                if (h > 0) {
                  el.style.height = h + "px";
                  el.style.minHeight = h + "px";
                }
              }
            };
            setHeight();
            requestAnimationFrame(setHeight);
            setTimeout(setHeight, 100);
            swiper.on("slideChangeTransitionEnd", setHeight);
          }
        },
      },
    });
  }
}

/**
 * Countdown timer (announcement bar / product)
 */
class CountdownTimer extends HTMLElement {
  constructor() {
    super();
    this.timeoutMessage = this.dataset.timeoutMessage;
    this.format = this.dataset.format || "dd:hh:mm:ss";
    this.interval = null;
    this.init();
  }

  init() {
    const cddl = this.dataset.endtime;
    const minutesLeft = this.dataset.minutesLeft;
    const countdownAppend = this.querySelector(".countdown-inner");
    if (countdownAppend) {
      countdownAppend.innerHTML = this.appendChildHtml(minutesLeft).innerHTML;
    } else {
      this.innerHTML = this.appendChildHtml(minutesLeft).innerHTML;
    }

    if (cddl) {
      let isoDate = "";
      if (this.isISODate(cddl)) {
        isoDate = cddl;
        this.mainFunction(isoDate, minutesLeft);
      } else {
        if (this.isValidDateTime(cddl)) {
          isoDate = new Date(cddl).toISOString();
          this.mainFunction(isoDate, minutesLeft);
        } else if (this.isValidDate(cddl)) {
          const dateParts = cddl.split("-");
          isoDate =
            dateParts[2] +
            "-" +
            dateParts[0].padStart(2, "0") +
            "-" +
            dateParts[1].padStart(2, "0") +
            "T00:00:00Z";
          this.mainFunction(isoDate, minutesLeft);
        } else {
          this.mainFunction(isoDate, minutesLeft);
        }
      }
    } else {
      this.mainFunction("", minutesLeft);
    }
  }

  mainFunction(isoDate, minutesLeft) {
    let timeLeft = {};
    const calculateTimeLeft = (difference) => {
      const result = {};
      if (this.format.includes("dd")) {
        result.days_timer = Math.floor(difference / (1000 * 60 * 60 * 24));
      }
      if (this.format.includes("hh")) {
        if (this.format.includes("dd")) {
          result.hours_timer = Math.floor((difference / (1000 * 60 * 60)) % 24);
        } else {
          result.hours_timer = Math.floor(difference / (1000 * 60 * 60));
        }
      }
      if (this.format.includes("mm")) {
        if (this.format === "mm:ss") {
          result.minutes_timer = Math.floor(difference / 1000 / 60);
        } else {
          result.minutes_timer = Math.floor((difference / 1000 / 60) % 60);
        }
      }
      if (this.format.includes("ss")) {
        result.seconds_timer = Math.floor((difference / 1000) % 60);
      }
      return result;
    };

    if (Date.parse(isoDate) || minutesLeft) {
      let deadline = new Date(isoDate);
      if (minutesLeft) {
        deadline = new Date(Date.now() + parseInt(minutesLeft, 10) * 60000);
      }
      const updateCountdown = () => {
        const now = new Date();
        const difference = deadline - now;
        timeLeft = difference > 0 ? calculateTimeLeft(difference) : calculateTimeLeft(0);

        if (
          (timeLeft.days_timer > 0 || timeLeft.hours_timer > 0 || timeLeft.minutes_timer > 0 || timeLeft.seconds_timer > 0) ||
          !this.timeoutMessage
        ) {
          Object.entries(timeLeft).forEach(([key, value]) => {
            const node = this.querySelector("." + key);
            if (node) node.textContent = String(value).padStart(2, "0");
          });
        } else if (this.timeoutMessage) {
          this.innerHTML = this.timeoutMessage;
          clearInterval(this.interval);
        } else {
          clearInterval(this.interval);
        }
        this.classList.remove("hidden");
      };
      this.interval = setInterval(updateCountdown, 1000);
      updateCountdown();
    } else {
      timeLeft = calculateTimeLeft(0);
      Object.entries(timeLeft).forEach(([key, value]) => {
        const node = this.querySelector("." + key);
        if (node) node.textContent = String(value).padStart(2, "0");
      });
    }
  }

  appendChildHtml(minutesLeft) {
    const days = this.dataset.days;
    const hours = this.dataset.hours;
    const mins = this.dataset.mins;
    const secs = this.dataset.secs;
    const container = document.createElement("div");
    container.innerHTML =
      (!minutesLeft
        ? (this.format.includes("dd")
            ? `<div class="countdown--container"><span class="days_timer count-timer"></span><span class="timer_announcementbar--text"> ${days || ""}</span></div>`
            : "") +
          (this.format.includes("hh")
            ? `<div class="countdown--container"><span class="hours_timer count-timer"></span><span class="timer_announcementbar--text"> ${hours || ""}</span></div>`
            : "")
        : "") +
      `<div class="countdown--container"><span class="minutes_timer count-timer"></span><span class="timer_announcementbar--text"> ${minutesLeft ? "m" : mins || ""}</span></div><div class="countdown--container"><span class="seconds_timer count-timer"></span><span class="timer_announcementbar--text"> ${minutesLeft ? "s" : secs || ""}</span></div>`;
    return container;
  }

  isISODate(dateString) {
    return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z$/.test(dateString);
  }
  isValidDateTime(dateString) {
    return /^\d{2}-\d{2}-\d{4} \d{2}:\d{2}:\d{2}$/.test(dateString);
  }
  isValidDate(dateString) {
    return /^\d{2}-\d{2}-\d{4}$/.test(dateString);
  }
}

if (!customElements.get("announcement-bar")) customElements.define("announcement-bar", AnnouncementBar);
if (!customElements.get("slide-section")) customElements.define("slide-section", SlideSection);
if (!customElements.get("countdown-timer")) customElements.define("countdown-timer", CountdownTimer);
